import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { BaseComponent } from '../../../common/base-component/base-component';
import { DataContextService } from '../../../services/datacontext.service';
import { SpaceAvailability, SpaceAvailabilityItem } from '../../../models/find-a-space.model';
import { SpaceFeature } from '../../../models/masterdata.model'
import { MRRecommendation } from 'src/app/models/meetings.model';
import { InBuildingSpaceAvailablity } from '../bulk/change-space/components/select-space/select-space';

@Component({
  selector: 'app-space-features',
  templateUrl: './space-features.component.html',
  styleUrls: ['./space-features.component.less'],
})
export class SpaceFeaturesComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() initialSelect: number[];

  @Output() featureChange = new EventEmitter<number[]>();

  @ViewChild('featuresContainer', { static: false }) featuresContainer: ElementRef<HTMLDivElement>;
  @ViewChild('leftSide', { static: false }) leftSide: ElementRef<HTMLDivElement>;
  @ViewChild('rightSide', { static: false }) rightSide: ElementRef<HTMLDivElement>;
  @ViewChildren('featureElement') featureElements: QueryList<ElementRef<HTMLDivElement>>;

  features: SpaceFeature[] = [];
  selectedQuantity: number;
  isExpanded: boolean;
  visibleFeatures: number;

  private maxVisibleCount: number;
  private readonly wrapperPadding = 60;
  private readonly featureGap = 8;

  get selected(): SpaceFeature[] {
    return this.features.filter((f) => f.selected);
  }

  constructor(private cdRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.getAllFeatures();
    this.setInitialState();
    this.sortFeatures();
  }

  ngOnChanges() {
    if (!this.isExpanded) {
      setTimeout(() => {
        this.calculateVisibilityCount();
      }, 0);
    }
  }

  ngAfterViewInit() {
    if (this.featuresContainer && this.featureElements && this.leftSide && this.rightSide) {
      const alreadyUsedSpace = this.leftSide.nativeElement.clientWidth + this.rightSide.nativeElement.clientWidth + this.wrapperPadding;
      let count = 0;
      const clientWidth = this.featuresContainer.nativeElement.clientWidth - alreadyUsedSpace;
      let totalWidth = 0;
      this.featureElements.forEach((el, index) => {
        this.features[index].width = el.nativeElement.clientWidth;
        totalWidth += el.nativeElement.clientWidth + this.featureGap;
        if (clientWidth - totalWidth > 10) {
          ++count;
        }
      });

      this.visibleFeatures = this.maxVisibleCount = count;
      this.cdRef.detectChanges();
    }
  }

  calculateFeaturesAmount(availableSpaces: SpaceAvailability | MRRecommendation | InBuildingSpaceAvailablity): void {
    if (!availableSpaces || !this.features.length) {
      return;
    }
    const featuresCountMap = {};
    this.features.forEach((f) => (featuresCountMap[f.id] = 0));

    availableSpaces.items.forEach((item) => {
      const features = item.roomFeatures || item.space.features;
      features.forEach((feature) => {
        const id = feature.id || feature;
        featuresCountMap[id] = featuresCountMap[id] ? ++featuresCountMap[id] : 1;
      });
    });

    this.features.forEach((feature: SpaceFeature) => {
      feature.amount = featuresCountMap[feature.id];
    });
  }

  toggleSelection(feature: SpaceFeature, index: number): void {
    if (index !== -1) {
      this.features[index].selected = !this.features[index].selected;
    }
    this.selectedQuantity = this.selected.length;
    this.featureChange.emit(this.selected.map((s) => s.id));
  }

  expand(state: boolean): void {
    this.isExpanded = state;
    if (state) {
      this.visibleFeatures = undefined;
    } else {
      setTimeout(() => {
        this.calculateVisibilityCount();
      }, 0);
    }
  }

  clearSelected(): void {
    this.features.forEach((f) => (f.selected = false));
    this.selectedQuantity = this.selected.length;
    this.featureChange.emit(this.selected.map((s) => s.id));
  }

  private calculateVisibilityCount(): void {
    if (this.featuresContainer && this.features.every((i) => !!i.width) && this.leftSide && this.rightSide) {
      const alreadyUsedSpace = this.leftSide.nativeElement.clientWidth + this.rightSide.nativeElement.clientWidth + this.wrapperPadding;
      const clientWidth = this.featuresContainer.nativeElement.clientWidth - alreadyUsedSpace;
      let count = 0;
      let totalWidth = 0;
      this.features.forEach((f) => {
        totalWidth += f.width + this.featureGap;
        if (clientWidth - totalWidth > 10) {
          ++count;
        }
      });

      if (count) {
        this.visibleFeatures = this.maxVisibleCount = count;
      }
      this.cdRef.detectChanges();
    }
  }

  private getAllFeatures(): void {
    this.features = DataContextService.SpaceFeatures.filter(feature => feature.iconKey).map((feature) => ({
      id: feature.id,
      name: feature.name,
      amount: 0,
      iconClass: feature.iconClass,
    }));
  }

  private setInitialState(): void {
    if (this.initialSelect) {
      const preferenceFeaturesMap = this.initialSelect.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
      this.features.forEach((f) => (f.selected = !!preferenceFeaturesMap[f.id]));
      this.selectedQuantity = this.selected.length;
      if (!this.isExpanded) {
        this.calculateVisibilityCount();
      }
    }
  }

  sortFeatures(): void {
    this.features = this.features
      .sort((a, b) => a.name.localeCompare(b.name))
      .sort((a, b) => b.amount - a.amount)
      .sort((a, b) => Number(b.selected) - Number(a.selected));
  }
}
